/* eslint-disable @typescript-eslint/no-explicit-any */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import type { TDocumentDefinitions, Content, Margins } from 'pdfmake/interfaces';
import { ICaixaMovimento } from '../../shared/services/api/Venda/CaixaTurnoService';

const setupFonts = () => {
  pdfMake.fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
  };
  pdfMake.vfs = (pdfFonts as any)?.pdfMake?.vfs || pdfFonts;
};

const formatMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const linhaSeparadora: Content = {
  text: '-'.repeat(48),
  alignment: 'center',
  fontSize: 8,
  margin: [0, 2, 0, 2] as Margins,
};

const gerarPdf = (content: Content[]): Promise<Blob> => {
  setupFonts();

  const documentDefinition: TDocumentDefinitions = {
    pageSize: { width: 226.77, height: 'auto' as const }, // 80mm
    // Margem esquerda/direita um pouco maior que a vertical — impressoras
    // térmicas reais costumam ter uma área imprimível mais estreita que os
    // 80mm nominais, cortando o que fica flush na borda (valores em negrito
    // à esquerda, valores alinhados à direita nas tabelas).
    pageMargins: [14, 10, 14, 10] as Margins,
    content,
    defaultStyle: { font: 'Roboto', fontSize: 9 },
    compress: true,
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
};

interface IComprovanteAbertura {
  operador: string;
  dataabertura: string;
  valorabertura: number;
}

// Comprovante de abertura de caixa — só o essencial: quem abriu, quando e com quanto.
export const PDFAberturaCaixa = (dados: IComprovanteAbertura): Promise<Blob> => {
  const content: Content[] = [
    { text: 'ABERTURA DE CAIXA', alignment: 'center', bold: true, fontSize: 12, margin: [0, 0, 0, 4] as Margins },
    linhaSeparadora,
    { text: `Operador: ${dados.operador}`, fontSize: 9 },
    { text: `Data/hora: ${moment(dados.dataabertura).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    linhaSeparadora,
    { text: 'Valor inicial', fontSize: 9, margin: [0, 4, 0, 0] as Margins },
    { text: `R$ ${formatMoeda(dados.valorabertura)}`, bold: true, fontSize: 14 },
    linhaSeparadora,
    { text: '*** DOCUMENTO SEM VALOR FISCAL ***', alignment: 'center', bold: true, fontSize: 8, margin: [0, 4, 0, 0] as Margins },
  ];

  return gerarPdf(content);
};

interface IComprovanteSangria {
  operador: string;
  datalan: string;
  valor: number;
  motivo?: string;
  numero?: number;
  tipo?: 'cofre' | 'pagamento';
}

// Comprovante de sangria — retirada de dinheiro (ou outra forma) do caixa
// durante o turno.
export const PDFSangriaCaixa = (dados: IComprovanteSangria): Promise<Blob> => {
  const content: Content[] = [
    { text: 'SANGRIA DE CAIXA', alignment: 'center', bold: true, fontSize: 12, margin: [0, 0, 0, 4] as Margins },
    linhaSeparadora,
    ...(dados.numero ? [{ text: `Suprimento nº ${dados.numero}`, fontSize: 9 } as Content] : []),
    { text: `Operador: ${dados.operador}`, fontSize: 9 },
    { text: `Data/hora: ${moment(dados.datalan).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    { text: `Tipo: ${dados.tipo === 'cofre' ? 'Para o cofre' : 'Para pagamento'}`, fontSize: 9 },
    ...(dados.motivo ? [{ text: `Motivo: ${dados.motivo}`, fontSize: 9 } as Content] : []),
    linhaSeparadora,
    { text: 'Valor retirado', fontSize: 9, margin: [0, 4, 0, 0] as Margins },
    { text: `R$ ${formatMoeda(dados.valor)}`, bold: true, fontSize: 14 },
    linhaSeparadora,
    { text: '*** DOCUMENTO SEM VALOR FISCAL ***', alignment: 'center', bold: true, fontSize: 8, margin: [0, 4, 0, 0] as Margins },
  ];

  return gerarPdf(content);
};

interface IComprovanteTransferencia {
  operador: string;
  destino: string;
  datalan: string;
  valor: number;
  motivo?: string;
  numero?: number;
}

// Comprovante de transferência de dinheiro do caixa do operador logado pro
// caixa de outro operador.
export const PDFTransferenciaCaixa = (dados: IComprovanteTransferencia): Promise<Blob> => {
  const content: Content[] = [
    { text: 'TRANSFERÊNCIA ENTRE CAIXAS', alignment: 'center', bold: true, fontSize: 12, margin: [0, 0, 0, 4] as Margins },
    linhaSeparadora,
    ...(dados.numero ? [{ text: `Suprimento nº ${dados.numero}`, fontSize: 9 } as Content] : []),
    { text: `Operador: ${dados.operador}`, fontSize: 9 },
    { text: `Destino: ${dados.destino}`, fontSize: 9 },
    { text: `Data/hora: ${moment(dados.datalan).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    ...(dados.motivo ? [{ text: `Motivo: ${dados.motivo}`, fontSize: 9 } as Content] : []),
    linhaSeparadora,
    { text: 'Valor transferido (Dinheiro)', fontSize: 9, margin: [0, 4, 0, 0] as Margins },
    { text: `R$ ${formatMoeda(dados.valor)}`, bold: true, fontSize: 14 },
    linhaSeparadora,
    { text: '*** DOCUMENTO SEM VALOR FISCAL ***', alignment: 'center', bold: true, fontSize: 8, margin: [0, 4, 0, 0] as Margins },
  ];

  return gerarPdf(content);
};

interface IComprovanteExtrato {
  operador: string;
  dataabertura: string;
  datareferencia: string;
  valorabertura: number;
  movimentos: ICaixaMovimento[];
  saldo: number;
  fechamento?: {
    contadestino: string;
    // Positivo = quebra (faltou dinheiro, debitado do caixa); negativo =
    // sobra (creditado no caixa) — ver fecharSessaoComConferencia no
    // backend. Omitido/zero = bateu certinho, não aparece linha nenhuma.
    diferenca?: number;
  };
}

// Agrupamento dinâmico pelo próprio texto do histórico — mesmo critério que
// o backend já usa pra "Recebimentos por forma de pagamento" na tela (ver
// extrairCategoriaDoHistorico em CategoriaPagamento.ts, backend). Categorias
// gravadas em `caixa.historico`: vendas no PDV (CreatePdv.ts) gravam a
// própria categoria completa (ex.: "Cartão Crédito", "PIX Dinâmico", sem
// prefixo/separador nenhum); baixas de contas a receber com forma de
// pagamento (contasreceber/Baixa.ts e BaixaParcial.ts) gravam "Categoria -
// vendas {resto}". Antes disso usava uma lista fixa de só 4 baldes
// (Dinheiro/PIX/Cartão/Outros) que juntava tudo com esse prefixo numa linha
// só — como as vendas no PDV não têm mais esse prefixo (vêm com a categoria
// completa), caíam despercebidas em "Outros" no impresso, mesmo já
// aparecendo certo na tela.
const VENDA_SEPARADOR_IMPRESSAO = ' - vendas ';

// Mesmo sufixo opcional " [editado em DD/MM/AAAA HH:MM]" que o backend
// acrescenta no historico de uma venda editada por fora do PDV (ver
// marcarHistoricoComoEditado em CategoriaPagamento.ts, backend) — precisa
// ser ignorado aqui também, senão cada edição vira uma categoria "nova"
// (texto diferente) nesses agrupamentos, duplicando a linha em vez de somar.
const MARCADOR_EDITADO_REGEX = /\s*\[editado em [^\]]+\]\s*$/;

const extrairCategoriaPagamento = (historicoOriginal: string | null | undefined): string => {
  if (!historicoOriginal) return 'Outros';
  const historico = historicoOriginal.replace(MARCADOR_EDITADO_REGEX, '');
  const indice = historico.indexOf(VENDA_SEPARADOR_IMPRESSAO);
  if (indice !== -1) return historico.slice(0, indice);
  return historico;
};

const calcularDetalhamentoPorFormaPagamento = (movimentos: ICaixaMovimento[]): Map<string, number> => {
  const totais = new Map<string, number>();
  movimentos
    .filter((mov) => (mov.tipo === 3 || mov.tipo === 1) && mov.entrada > 0)
    .forEach((mov) => {
      const categoria = extrairCategoriaPagamento(mov.historico);
      totais.set(categoria, (totais.get(categoria) || 0) + mov.entrada);
    });
  return totais;
};

// Só vendas do PDV (tipo 3 — ver CreatePdv.ts) — diferente de
// calcularDetalhamentoPorFormaPagamento acima, que também soma baixas de
// contas a receber feitas fora do PDV (tipo 1, ex.: parcela de crediário
// paga na tela de Faturamento). Essa aqui é "quanto vendi hoje, por forma de
// pagamento", sem misturar com outros recebimentos — pedido explícito, pra
// não confundir com o detalhamento geral.
const calcularVendasPorFormaPagamento = (movimentos: ICaixaMovimento[]): Map<string, number> => {
  const totais = new Map<string, number>();
  movimentos
    .filter((mov) => mov.tipo === 3 && mov.entrada > 0)
    .forEach((mov) => {
      const categoria = extrairCategoriaPagamento(mov.historico);
      totais.set(categoria, (totais.get(categoria) || 0) + mov.entrada);
    });
  return totais;
};

// Extrato/fluxo do turno — usado tanto para "imprimir agora" (a qualquer
// momento, sem fechar) quanto para o comprovante final de fechamento
// (mesma estrutura, só muda o título/rodapé quando `fechamento` é informado).
export const PDFExtratoCaixa = (dados: IComprovanteExtrato): Promise<Blob> => {
  const titulo = dados.fechamento ? 'FECHAMENTO DE CAIXA' : 'FLUXO DE CAIXA';

  // Extrato lê como um "livro caixa": mais antigo primeiro. Se houver
  // fechamento, ele é cronologicamente o último evento do turno — por isso
  // a seção de fechamento abaixo fica no final do documento, não aqui na
  // lista (o lançamento de fechamento em si ainda não existe quando esse PDF
  // é gerado, ver DialogFluxoCaixa.tsx).
  const movimentosOrdenados = [...dados.movimentos].sort(
    (a, b) => new Date(a.datalan).getTime() - new Date(b.datalan).getTime()
  );

  const linhasMovimentos: Content[] = movimentosOrdenados.map((mov) => ({
    columns: [
      { text: moment(mov.datalan).format('DD/MM HH:mm'), fontSize: 7, width: 55 },
      { text: mov.historico || '-', fontSize: 7, width: '*' },
      {
        text: mov.entrada > 0 ? `+${formatMoeda(mov.entrada)}` : `-${formatMoeda(mov.saida)}`,
        fontSize: 7,
        width: 55,
        alignment: 'right',
        color: mov.entrada > 0 ? undefined : '#B71C1C',
      },
    ],
    margin: [0, 1, 0, 1] as Margins,
  }));

  const totaisPorCategoria = calcularDetalhamentoPorFormaPagamento(dados.movimentos);
  const linhasDetalhamento: Content[] = Array.from(totaisPorCategoria.entries())
    .filter(([, valor]) => valor > 0)
    .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
    .map(([categoria, valor]) => ({
      columns: [
        { text: categoria, fontSize: 8 },
        { text: `R$ ${formatMoeda(valor)}`, fontSize: 8, alignment: 'right' },
      ],
      margin: [0, 1, 0, 1] as Margins,
    }));

  const totaisVendasPorCategoria = calcularVendasPorFormaPagamento(dados.movimentos);
  const totalVendas = Array.from(totaisVendasPorCategoria.values()).reduce((acc, v) => acc + v, 0);
  const linhasVendasPorFormaPagamento: Content[] = Array.from(totaisVendasPorCategoria.entries())
    .filter(([, valor]) => valor > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([categoria, valor]) => ({
      columns: [
        { text: categoria, fontSize: 8 },
        { text: `R$ ${formatMoeda(valor)}`, fontSize: 8, alignment: 'right' },
      ],
      margin: [0, 1, 0, 1] as Margins,
    }));

  const content: Content[] = [
    { text: titulo, alignment: 'center', bold: true, fontSize: 12, margin: [0, 0, 0, 4] as Margins },
    linhaSeparadora,
    { text: `Operador: ${dados.operador}`, fontSize: 9 },
    { text: `Abertura: ${moment(dados.dataabertura).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    ...(!dados.fechamento
      ? [{ text: `Emitido em: ${moment(dados.datareferencia).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 } as Content]
      : []),
    linhaSeparadora,
    { text: 'Movimentações', bold: true, fontSize: 9, margin: [0, 0, 0, 2] as Margins },
    ...(linhasMovimentos.length > 0
      ? linhasMovimentos
      : [{ text: 'Nenhuma movimentação no período.', fontSize: 8, italics: true } as Content]),
    ...(linhasVendasPorFormaPagamento.length > 0
      ? [
        linhaSeparadora,
        { text: 'Vendas por forma de pagamento', bold: true, fontSize: 9, margin: [0, 0, 0, 2] as Margins } as Content,
        ...linhasVendasPorFormaPagamento,
        {
          columns: [
            { text: 'Total vendido', fontSize: 9, bold: true },
            { text: `R$ ${formatMoeda(totalVendas)}`, fontSize: 9, bold: true, alignment: 'right' },
          ],
          margin: [0, 3, 0, 0] as Margins,
        } as Content,
      ]
      : []),
    linhaSeparadora,
    { text: 'Valor inicial', fontSize: 9 },
    { text: `R$ ${formatMoeda(dados.valorabertura)}`, fontSize: 10 },
    { text: dados.fechamento ? 'Saldo transferido' : 'Saldo atual', fontSize: 9, bold: true, margin: [0, 4, 0, 0] as Margins },
    { text: `R$ ${formatMoeda(dados.saldo)}`, bold: true, fontSize: 14 },
    ...(linhasDetalhamento.length > 0
      ? [
        linhaSeparadora,
        { text: 'Recebimentos por forma de pagamento', bold: true, fontSize: 9, margin: [0, 0, 0, 2] as Margins } as Content,
        ...linhasDetalhamento,
      ]
      : []),
    ...(dados.fechamento
      ? [
        linhaSeparadora,
        { text: `Fechamento: ${moment(dados.datareferencia).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 } as Content,
        { text: `Transferido para: ${dados.fechamento.contadestino}`, fontSize: 9, margin: [0, 2, 0, 0] as Margins } as Content,
        ...(dados.fechamento.diferenca && Math.abs(dados.fechamento.diferenca) > 0.004
          ? [{
            text: dados.fechamento.diferenca > 0
              ? `Quebra de caixa: R$ ${formatMoeda(dados.fechamento.diferenca)}`
              : `Sobra de caixa: R$ ${formatMoeda(Math.abs(dados.fechamento.diferenca))}`,
            fontSize: 9,
            bold: true,
            color: dados.fechamento.diferenca > 0 ? '#B71C1C' : '#2E7D32',
            margin: [0, 2, 0, 0] as Margins,
          } as Content]
          : []),
      ]
      : []),
    linhaSeparadora,
    { text: '*** DOCUMENTO SEM VALOR FISCAL ***', alignment: 'center', bold: true, fontSize: 8, margin: [0, 4, 0, 0] as Margins },
  ];

  return gerarPdf(content);
};

export interface IVendaRelatorioSessao {
  numero: string;
  emissao: string;
  total: number;
  nomeParceiro: string;
  descricaoOperacao: string;
}

interface IComprovanteRelatorioVendasSessao {
  operador: string;
  dataabertura: string;
  vendas: IVendaRelatorioSessao[];
}

// Relatório de vendas da sessão — botão "Relatório" no rodapé do PDV. Só as
// vendas (tipovenda='pdv'), não o fluxo de caixa inteiro (que já tem o
// extrato acima, com abertura/sangria/fechamento também).
export const PDFRelatorioVendasSessao = (dados: IComprovanteRelatorioVendasSessao): Promise<Blob> => {
  const totalVendas = dados.vendas.reduce((acc, v) => acc + v.total, 0);

  const linhasVendas: Content[] = dados.vendas.map((venda) => ({
    stack: [
      {
        columns: [
          { text: `Nº ${venda.numero}`, fontSize: 7, bold: true, width: '*' },
          { text: `R$ ${formatMoeda(venda.total)}`, fontSize: 7, bold: true, width: 55, alignment: 'right' },
        ],
      },
      { text: `${moment(venda.emissao).format('DD/MM HH:mm')} • ${venda.nomeParceiro}`, fontSize: 7 },
    ],
    margin: [0, 1, 0, 3] as Margins,
  }));

  const content: Content[] = [
    { text: 'RELATÓRIO DE VENDAS', alignment: 'center', bold: true, fontSize: 12, margin: [0, 0, 0, 4] as Margins },
    linhaSeparadora,
    { text: `Operador: ${dados.operador}`, fontSize: 9 },
    { text: `Abertura: ${moment(dados.dataabertura).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    { text: `Emitido em: ${moment().format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    linhaSeparadora,
    { text: 'Vendas', bold: true, fontSize: 9, margin: [0, 0, 0, 2] as Margins },
    ...(linhasVendas.length > 0
      ? linhasVendas
      : [{ text: 'Nenhuma venda nesta sessão ainda.', fontSize: 8, italics: true } as Content]),
    linhaSeparadora,
    { text: `Total (${dados.vendas.length} venda${dados.vendas.length === 1 ? '' : 's'})`, fontSize: 9, bold: true, margin: [0, 4, 0, 0] as Margins },
    { text: `R$ ${formatMoeda(totalVendas)}`, bold: true, fontSize: 14 },
    linhaSeparadora,
    { text: '*** DOCUMENTO SEM VALOR FISCAL ***', alignment: 'center', bold: true, fontSize: 8, margin: [0, 4, 0, 0] as Margins },
  ];

  return gerarPdf(content);
};

interface IVendaResumoImpressao {
  numero: string;
  emissao: string;
  nomeParceiro: string;
  quantidadeItens: number;
  vprod: number;
  desconto: number;
  acrescimo: number;
  total: number;
  pagamentos: { categoria: string; valor: number }[];
  // undefined = venda ativa normal. Cancelada/devolvida continua aparecendo
  // na relação (nunca é removida, ver GetResumoVendasSessao.ts), só marcada
  // — não pode imprimir como se fosse uma venda válida igual às outras.
  status?: 'cancelada' | 'devolvida';
}

interface IComprovanteResumoVendasPDV {
  nomeCaixa: string;
  operador: string;
  dataabertura: string;
  quantidadeVendas: number;
  quantidadeItens: number;
  produtosDiferentes: number;
  valorBruto: number;
  descontos: number;
  acrescimos: number;
  total: number;
  ticketMedio: number;
  formasPagamento: { categoria: string; quantidade: number; total: number }[];
  vendas: IVendaResumoImpressao[];
}

// Botão "Imprimir resumo" da dialog "Resumo de Vendas do PDV" — mesmo padrão
// (recibo 80mm, gerarPdf) do resto deste arquivo, com os totais/formas de
// pagamento/relação de vendas já calculados pelo backend (ver
// GetResumoVendasSessao.ts) e passados prontos aqui.
export const PDFResumoVendasPDV = (dados: IComprovanteResumoVendasPDV): Promise<Blob> => {
  const linhasVendas: Content[] = dados.vendas.map((venda) => {
    const formaPagamentoTexto = venda.pagamentos.length > 0
      ? venda.pagamentos.map((p) => p.categoria).join(' + ')
      : '-';
    const corStatus = venda.status === 'cancelada' ? '#B71C1C' : '#F57C00';
    return {
      stack: [
        {
          columns: [
            { text: `Nº ${venda.numero}`, fontSize: 7, bold: true, width: '*' },
            venda.status
              ? { text: venda.status === 'cancelada' ? 'CANCELADA' : 'DEVOLVIDA', fontSize: 7, bold: true, width: 55, alignment: 'right', color: corStatus }
              : { text: `R$ ${formatMoeda(venda.total)}`, fontSize: 7, bold: true, width: 55, alignment: 'right' },
          ],
        },
        { text: `${moment(venda.emissao).format('DD/MM HH:mm')} • ${venda.nomeParceiro} • ${venda.quantidadeItens} ite${venda.quantidadeItens === 1 ? 'm' : 'ns'}`, fontSize: 7 },
        ...(venda.status
          ? [{ text: `Valor estornado: R$ ${formatMoeda(venda.total)}`, fontSize: 6, italics: true, color: corStatus } as Content]
          : [{ text: formaPagamentoTexto, fontSize: 7, italics: true } as Content]),
      ],
      margin: [0, 1, 0, 3] as Margins,
    };
  });

  const linhasFormasPagamento: Content[] = dados.formasPagamento.map((forma) => ({
    columns: [
      { text: forma.categoria, fontSize: 8, width: '*' },
      { text: `${forma.quantidade}x`, fontSize: 8, width: 30, alignment: 'right' },
      { text: `R$ ${formatMoeda(forma.total)}`, fontSize: 8, width: 60, alignment: 'right' },
    ],
    margin: [0, 0, 0, 1] as Margins,
  }));

  const content: Content[] = [
    { text: 'RESUMO DE VENDAS DO PDV', alignment: 'center', bold: true, fontSize: 12, margin: [0, 0, 0, 4] as Margins },
    linhaSeparadora,
    { text: `Caixa: ${dados.nomeCaixa}`, fontSize: 9 },
    { text: `Operador: ${dados.operador}`, fontSize: 9 },
    { text: `Abertura: ${moment(dados.dataabertura).format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    { text: `Emitido em: ${moment().format('DD/MM/YYYY HH:mm:ss')}`, fontSize: 9 },
    linhaSeparadora,
    { text: `Vendas realizadas: ${dados.quantidadeVendas}`, fontSize: 9 },
    { text: `Itens vendidos: ${dados.quantidadeItens} (${dados.produtosDiferentes} produtos diferentes)`, fontSize: 9 },
    { text: `Valor bruto: R$ ${formatMoeda(dados.valorBruto)}`, fontSize: 9 },
    { text: `Descontos: R$ ${formatMoeda(dados.descontos)}`, fontSize: 9 },
    { text: `Acréscimos: R$ ${formatMoeda(dados.acrescimos)}`, fontSize: 9 },
    { text: `Ticket médio: R$ ${formatMoeda(dados.ticketMedio)}`, fontSize: 9 },
    { text: `Total vendido: R$ ${formatMoeda(dados.total)}`, fontSize: 11, bold: true, margin: [0, 2, 0, 0] as Margins },
    linhaSeparadora,
    { text: 'Totais por forma de pagamento', bold: true, fontSize: 9, margin: [0, 0, 0, 2] as Margins },
    ...(linhasFormasPagamento.length > 0
      ? linhasFormasPagamento
      : [{ text: 'Nenhum pagamento registrado.', fontSize: 8, italics: true } as Content]),
    linhaSeparadora,
    { text: 'Relação de vendas', bold: true, fontSize: 9, margin: [0, 0, 0, 2] as Margins },
    ...(linhasVendas.length > 0
      ? linhasVendas
      : [{ text: 'Nenhuma venda nesta sessão ainda.', fontSize: 8, italics: true } as Content]),
    linhaSeparadora,
    { text: '*** DOCUMENTO SEM VALOR FISCAL ***', alignment: 'center', bold: true, fontSize: 8, margin: [0, 4, 0, 0] as Margins },
  ];

  return gerarPdf(content);
};
